/**
 * Technology currency (2026-10-20).
 *
 * Is the technology each service rests on still supported by its maker?
 * Every configuration item is judged against the bank's technology
 * lifecycle register and rolled up to its service, worst first, with
 * the services behind the minimum viable bank counted apart. An item
 * the register cannot judge says why and is never read as current.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { api, type ApiError } from "shared/api/client";
import { MarsadIcon } from "shared/components/MarsadIcon";

const P = "technologyCurrency";

type Verdict = "out_of_support" | "extended_support" | "not_assessable" | "in_support";
const VERDICTS: Verdict[] = [
  "out_of_support",
  "extended_support",
  "not_assessable",
  "in_support",
];

interface CurrencyItem {
  id: string;
  name: string;
  ci_class: string | null;
  manufacturer: string | null;
  model: string | null;
  verdict: Verdict;
  reason: "no_model" | "no_register_entry" | "no_dates" | null;
  decided_by: string | null;
  source: string | null;
}

interface CurrencyService {
  id: string;
  name: string;
  mvb: boolean;
  verdict: Verdict;
  counts: Record<Verdict, number>;
  configuration_items: CurrencyItem[];
}

export interface TechnologyCurrency {
  as_of: string;
  register_products: number;
  summary: {
    configuration_items: number;
    by_verdict: Record<Verdict, number>;
    unlinked_configuration_items: number;
  };
  mvb: {
    services: number;
    configuration_items: number;
    by_verdict: Record<Verdict, number>;
  };
  services: CurrencyService[];
}

const TONE: Record<Verdict, string> = {
  out_of_support: "bg-rose-50 text-rose-800",
  extended_support: "bg-amber-50 text-amber-800",
  not_assessable: "bg-slate-100 text-slate-700",
  in_support: "bg-emerald-50 text-emerald-800",
};

function VerdictPill({ verdict }: { verdict: Verdict }) {
  const { t } = useTranslation();
  return (
    <span
      data-testid="currency-verdict"
      data-verdict={verdict}
      className={`rounded-full px-2 py-0.5 text-xs ${TONE[verdict]}`}
    >
      {t(`${P}.verdict.${verdict}`)}
    </span>
  );
}

function Counts({ by }: { by: Record<Verdict, number> }) {
  const { t } = useTranslation();
  return (
    <span className="flex flex-wrap gap-2">
      {VERDICTS.map((v) => (
        <span key={v} className={`rounded-full px-2 py-0.5 text-xs ${TONE[v]}`}>
          {t(`${P}.count.${v}`, { count: by[v] ?? 0 })}
        </span>
      ))}
    </span>
  );
}

function ServiceRow({ s }: { s: CurrencyService }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <li
      data-testid="currency-service"
      className="rounded border border-slate-200 bg-white p-2 text-sm"
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full flex-wrap items-center gap-2 text-start"
      >
        <span className="font-medium text-slate-900">{s.name}</span>
        {s.mvb && (
          <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-xs text-indigo-800">
            {t(`${P}.mvb`)}
          </span>
        )}
        <VerdictPill verdict={s.verdict} />
        <span className="ms-auto text-xs text-slate-500">
          {t(`${P}.items`, { count: s.configuration_items.length })}
        </span>
      </button>
      {open && (
        <ul className="mt-2 space-y-1 border-t border-slate-100 pt-2">
          {s.configuration_items.map((c) => (
            <li
              key={c.id}
              data-testid="currency-item"
              className="flex flex-wrap items-center gap-2 text-xs"
            >
              <VerdictPill verdict={c.verdict} />
              <span className="text-slate-900">{c.name}</span>
              <span className="text-slate-600">
                {c.model ? `${c.manufacturer ?? ""} ${c.model}` : t(`${P}.noModel`)}
              </span>
              <span className="text-slate-500">
                {c.reason
                  ? t(`${P}.reason.${c.reason}`)
                  : c.decided_by
                    ? t(
                        c.verdict === "out_of_support" ? `${P}.endedOn` : `${P}.endsOn`,
                        { date: c.decided_by },
                      )
                    : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export function TechnologyCurrencyView() {
  const { t } = useTranslation();
  const q = useQuery<TechnologyCurrency, ApiError>({
    queryKey: ["twin", "technology-currency"],
    queryFn: () => api.get<TechnologyCurrency>("/v1/twin/technology-currency"),
  });
  const data =
    q.data && Array.isArray(q.data.services) && q.data.summary ? q.data : null;
  return (
    <article className="space-y-4" data-testid="technology-currency-page">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 font-display text-2xl text-slate-900">
          <MarsadIcon name="telemetry" className="text-brand-brass" />
          {t(`${P}.heading`)}
        </h1>
        <p className="text-sm text-slate-600">{t(`${P}.standfirst`)}</p>
      </header>
      {q.isError && (
        <p role="alert" className="text-sm text-rose-800">
          {q.error.detail}
        </p>
      )}
      {data && data.register_products === 0 && (
        <p data-testid="currency-register-empty" className="text-sm text-slate-700">
          {t(`${P}.registerEmpty`)}{" "}
          <Link
            to="/foundation/admin/technology-lifecycle"
            className="text-brand-brassDark hover:underline"
          >
            {t(`${P}.registerLink`)}
          </Link>
        </p>
      )}
      {data && (
        <>
          <section className="space-y-2 rounded border border-slate-200 bg-white p-3 text-sm">
            <p data-testid="currency-estate">
              {t(`${P}.estate`, {
                count: data.summary.configuration_items,
                products: data.register_products,
              })}
            </p>
            <Counts by={data.summary.by_verdict} />
            <p data-testid="currency-mvb" className="pt-1">
              {t(`${P}.mvbLine`, { count: data.mvb.configuration_items })}
            </p>
            <Counts by={data.mvb.by_verdict} />
            <p className="text-xs text-slate-500">
              <Link
                to="/foundation/admin/technology-lifecycle"
                className="text-brand-brassDark hover:underline"
              >
                {t(`${P}.registerLink`)}
              </Link>
            </p>
          </section>
          <ul className="space-y-1">
            {data.services.map((s) => (
              <ServiceRow key={s.id} s={s} />
            ))}
          </ul>
        </>
      )}
    </article>
  );
}
